// One definition of the channel taxonomy, shared by the write path (signal
// payloads) and the read path (backend generates its SQL from these constants).
// Order IS precedence order. Both evaluators walk it top-down, so moving an
// entry changes classification. See classifyAcquisitionChannel.
export const ACQUISITION_CHANNELS = [
    'paid_search',
    'email',
    'ai_assistant',
    'organic_search',
    'social',
    'referral',
    'direct',
];
export const ACQUISITION_CHANNEL_LABELS = {
    paid_search: 'Paid Search',
    email: 'Email',
    ai_assistant: 'AI Assistant',
    organic_search: 'Organic Search',
    social: 'Social',
    referral: 'Referral',
    direct: 'Direct',
};
// Hostnames a vendor published as observed AI referrers (Plausible, own traffic).
export const AI_ASSISTANT_HOSTS_SOURCED = [
    'chatgpt.com',
    'chat.openai.com',
    'perplexity.ai',
    'claude.ai',
    'phind.com',
];
// Guessed from product names. GA4 and Matomo name the products but publish no
// host list, so confirm each against a real referrer before trusting a 0 count.
export const AI_ASSISTANT_HOSTS_INFERRED = [
    'gemini.google.com',
    'copilot.microsoft.com',
    'deepseek.com',
    'grok.com',
    'x.ai',
    'you.com',
    'poe.com',
];
export const AI_ASSISTANT_HOSTS = [
    ...AI_ASSISTANT_HOSTS_SOURCED,
    ...AI_ASSISTANT_HOSTS_INFERRED,
];
// Google ccTLDs are listed one by one because the matcher must also be
// expressible in ClickHouse SQL, where a public-suffix lookup is not available.
export const SEARCH_HOSTS = [
    'google.com',
    'google.co.uk',
    'google.de',
    'google.fr',
    'google.ca',
    'google.com.au',
    'bing.com',
    'duckduckgo.com',
    'search.yahoo.com',
    'yahoo.com',
    'ecosia.org',
    'startpage.com',
    'search.brave.com',
    'baidu.com',
    'yandex.com',
];
export const SOCIAL_HOSTS = [
    'facebook.com',
    'linkedin.com',
    'lnkd.in',
    't.co',
    'x.com',
    'twitter.com',
    'reddit.com',
    'instagram.com',
    'youtube.com',
    'tiktok.com',
    'news.ycombinator.com',
];
// utm_source arrives as a hostname ('chatgpt.com') or a bare token ('google').
export const AI_ASSISTANT_SOURCE_TOKENS = [
    'chatgpt',
    'openai',
    'perplexity',
    'claude',
    'gemini',
    'copilot',
    'deepseek',
    'grok',
    'phind',
    'poe',
];
export const SEARCH_SOURCE_TOKENS = [
    'google',
    'bing',
    'duckduckgo',
    'yahoo',
    'ecosia',
    'baidu',
    'yandex',
    'brave',
];
export const SOCIAL_SOURCE_TOKENS = [
    'facebook',
    'linkedin',
    'twitter',
    'reddit',
    'instagram',
    'youtube',
    'tiktok',
];
// GA4's paid regex is `^(.*cp.*|ppc|retargeting|paid.*)$`. Listed explicitly
// because a regex that loose matches any medium merely containing "cp".
export const PAID_MEDIUMS = [
    'cpc',
    'ppc',
    'cpm',
    'cpv',
    'cpa',
    'paid',
    'paidsearch',
    'paid_search',
    'retargeting',
];
export const EMAIL_TOKENS = [
    'email',
    'e-mail',
    'e_mail',
    'e mail',
    'newsletter',
];
export const ORGANIC_MEDIUM = 'organic';
// The Google members of SEARCH_HOSTS. Search Console covers Google only, so
// its report joins must not credit Bing or DuckDuckGo sessions.
export const GOOGLE_SEARCH_HOSTS = SEARCH_HOSTS.filter((host) => host === 'google.com' || host.startsWith('google.'));
export const GOOGLE_SOURCE_TOKEN = 'google';
// Auto-tagged ad clicks carry only these landing-URL params (on by default,
// per https://support.google.com/google-ads/answer/3095550), not utm_medium.
export const GOOGLE_ADS_CLICK_PARAMS = ['gclid', 'gbraid', 'wbraid'];
// Dotless hosts (`localhost`, intranet names) return null so this matches
// ClickHouse `domainWithoutWWW`, which drops them but keeps bare IPs.
export function normalizeAcquisitionHost(value) {
    if (typeof value !== 'string')
        return null;
    const trimmed = value.trim().toLowerCase();
    if (!trimmed)
        return null;
    const withoutScheme = trimmed.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
    const host = withoutScheme.split(/[/?#]/)[0] ?? '';
    const withoutPort = host.split(':')[0] ?? '';
    const bare = withoutPort.replace(/^www\./, '');
    if (!bare || !bare.includes('.'))
        return null;
    return bare;
}
// utm_source is also matched as a bare brand token ('google'), which is not a
// host, so token comparison keeps its own normalizer.
function normalizeToken(value) {
    if (typeof value !== 'string')
        return null;
    const trimmed = value.trim().toLowerCase();
    return trimmed || null;
}
// Exact host or a true subdomain, never a substring, so `notchatgpt.com` and
// `chatgpt.com.evil.io` cannot read as ChatGPT.
function hostMatches(host, entries) {
    if (!host)
        return false;
    return entries.some((entry) => host === entry || host.endsWith(`.${entry}`));
}
function tokenMatches(value, tokens) {
    return value !== null && tokens.includes(value);
}
function normalizeMedium(value) {
    if (typeof value !== 'string')
        return null;
    const trimmed = value.trim().toLowerCase();
    return trimmed || null;
}
/** Derive the acquisition channel. Pure and total. */
export function classifyAcquisitionChannel(input) {
    // utm_source is matched as well as the referrer, because ChatGPT stamps
    // `utm_source=chatgpt.com` and AI referrals often carry no referrer at all.
    const referrerHost = normalizeAcquisitionHost(input.referrer);
    const sourceHost = normalizeAcquisitionHost(input.utmSource);
    const sourceToken = normalizeToken(input.utmSource);
    const medium = normalizeMedium(input.utmMedium);
    const isSearch = hostMatches(referrerHost, SEARCH_HOSTS) ||
        hostMatches(sourceHost, SEARCH_HOSTS) ||
        tokenMatches(sourceToken, SEARCH_SOURCE_TOKENS);
    // Paid MUST precede organic: GA4's organic rule is a superset of its paid
    // rule, so organic-first swallows ad clicks and reports paid traffic as free.
    if (isSearch && tokenMatches(medium, PAID_MEDIUMS))
        return 'paid_search';
    if (tokenMatches(medium, EMAIL_TOKENS) ||
        tokenMatches(sourceToken, EMAIL_TOKENS)) {
        return 'email';
    }
    if (hostMatches(referrerHost, AI_ASSISTANT_HOSTS) ||
        hostMatches(sourceHost, AI_ASSISTANT_HOSTS) ||
        tokenMatches(sourceToken, AI_ASSISTANT_SOURCE_TOKENS)) {
        return 'ai_assistant';
    }
    if (isSearch || medium === ORGANIC_MEDIUM)
        return 'organic_search';
    if (hostMatches(referrerHost, SOCIAL_HOSTS) ||
        hostMatches(sourceHost, SOCIAL_HOSTS) ||
        tokenMatches(sourceToken, SOCIAL_SOURCE_TOKENS)) {
        return 'social';
    }
    if (referrerHost || sourceHost)
        return 'referral';
    return 'direct';
}
export function acquisitionChannelLabel(channel) {
    return ACQUISITION_CHANNEL_LABELS[channel] ?? 'Unknown';
}
//# sourceMappingURL=acquisition-channel.js.map